import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Form, Alert, Spinner, Tab, Nav } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import EmailApi from '../../api/EmailApi';
import DataApi from '../../api/DataApi';

const BulkEmailForm = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Get selected records from location state
  const { selectedRecords = [], objectName = 'lead' } = location.state || {};

  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [previewContent, setPreviewContent] = useState({ subject: '', body: '' });
  const [loading, setLoading] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showAllRecords, setShowAllRecords] = useState(false);
  const [previewRecordIndex, setPreviewRecordIndex] = useState(0);

  // Load templates when page loads
  useEffect(() => {
    if (selectedRecords.length > 0) {
      loadTemplates();
    }
  }, [selectedRecords]);

  const loadTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const emailApi = new EmailApi();
      const response = await emailApi.getEmailTemplates(objectName);
      setTemplates(response.data || []);
    } catch (err) {
      console.error('Error loading templates:', err);
      setError('Failed to load email templates');
    } finally {
      setLoadingTemplates(false);
    }
  };

  const updatePreviewForRecord = async (record) => {
    if (!selectedTemplate || !record) return;

    // setLoading(true);
    setError(null);

    try {
      const emailApi = new EmailApi();
      const recordId = record.id && typeof record.id === 'object' ? record.id.value || record.id.label : record.id;
      const { data } = await emailApi.create({
        templateId: selectedTemplate,
        recordId,
        objectName,
      }, 'test/parse-email-template');
      console.log("data", data);

      if (data.result) {
        setPreviewContent({
          subject: data.result?.subject || '',
          body: data.result?.body || ''
        });
      }
    } catch (err) {
      console.error('Error loading template preview:', err);
      setError('Failed to load template preview');
      setPreviewContent({ subject: '', body: '' });
    } finally {
      setLoading(false);
    }
  };

  // Filter records that have an email field
  const filteredRecords = selectedRecords ? selectedRecords.filter(record => {
    const emailValue = record.email && typeof record.email === 'object' ? record.email.value : record.email;
    return emailValue && emailValue.trim() !== '';
  }) : [];

  const recordCount = filteredRecords.length;

  const handleTemplateChange = async (templateId) => {
    setSelectedTemplate(templateId);

    if (templateId) {
      // Load preview for the selected template
      setLoading(true);
      setError(null);

      try {
        const emailApi = new EmailApi();
        // Get the selected record for preview
        if (filteredRecords && filteredRecords.length > 0 && filteredRecords[previewRecordIndex]) {
          const sampleRecord = filteredRecords[previewRecordIndex];
          const recordId = sampleRecord.id && typeof sampleRecord.id === 'object' ? sampleRecord.id.value || sampleRecord.id.label : sampleRecord.id;
          const { data } = await emailApi.create({
            templateId,
            recordId,
            objectName,
          }, 'test/parse-email-template');
          console.log("data", data);

          if (data.result) {
            setPreviewContent({
              subject: data.result?.subject || '',
              body: data.result?.body || ''
            });
          }
        }
      } catch (err) {
        console.error('Error loading template preview:', err);
        setError('Failed to load template preview');
        setPreviewContent({ subject: '', body: '' });
      } finally {
        setLoading(false);
      }
    } else {
      setPreviewContent({ subject: '', body: '' });
    }
  };



  const handleSendBulkEmail = async () => {
    if (!selectedTemplate) {
      setError('Please select an email template');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const emailApi = new EmailApi();
      // Process only records with valid email addresses
      const processedRecords = filteredRecords.map(record => ({
        ...record,
        id: record.id && typeof record.id === 'object' ? record.id.value || record.id.label : record.id,
        name: record.name && typeof record.name === 'object' ? record.name.value || record.name.label : record.name,
        email: record.email && typeof record.email === 'object' ? record.email.value : record.email
      }));
      const bulkEmailData = {
        records: processedRecords,
        templateId: selectedTemplate,
        objectName: objectName
      };

      const response = await emailApi.sendBulkEmail(bulkEmailData);

      setSuccess(`${response.data.success} emails sent successfully. ${response.data.errors} failed.`);
      // setTimeout(() => {
      //   navigate(-1);
      // }, 1500);
    } catch (err) {
      console.error('Error sending bulk email:', err);
      setError(err.message || 'Failed to send bulk emails');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    // Navigate back to the previous page
    navigate(-1);
  };

  if (selectedRecords.length === 0) {
    return (
      <Container className="mt-4">
        <Row className="justify-content-center">
          <Col md={8}>
            <Alert variant="danger">
              <h5>Error</h5>
              <p>No records selected for bulk email. Please select records from the list view first.</p>
              <Button onClick={handleCancel}>Go Back</Button>
            </Alert>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container fluid className="mt-4 px-4">
      <Row>
        <Col>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5>Bulk Email - {objectName}</h5>
              <div>

                <Button
                  variant="secondary"
                  onClick={handleSendBulkEmail}
                  disabled={loading || !selectedTemplate || recordCount === 0}
                >
                  {loading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Sending...
                    </>
                  ) : (
                    `Send to ${recordCount} Records`
                  )}
                </Button>
              </div>
            </Card.Header>
            <Card.Body>


              {error && (
                <Alert variant="danger" dismissible onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}

              {success && (
                <Alert variant="success">
                  {success}
                </Alert>
              )}

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Selected Records: <strong>{recordCount}</strong></Form.Label>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Email Template *</Form.Label>
                    {loadingTemplates ? (
                      <div className="d-flex align-items-center">
                        <Spinner animation="border" size="sm" className="me-2" />
                        Loading templates...
                      </div>
                    ) : (
                      <Form.Select
                        value={selectedTemplate}
                        onChange={(e) => handleTemplateChange(e.target.value)}
                        disabled={loading}
                      >
                        <option value="">Choose a template</option>
                        {templates.map((template) => (
                          <option key={template.id} value={template.id}>
                            {template.name}
                          </option>
                        ))}
                      </Form.Select>
                    )}
                  </Form.Group>

                  <div className="mt-3">
                    <h6>Template Preview</h6>
                    {selectedTemplate ? (
                      <>
                        <div className="border rounded p-3 bg-light mb-2">
                          <small className="text-muted">Subject Preview:</small>
                          <p className="mb-1"><strong>{previewContent.subject || '(Empty)'}</strong></p>
                        </div>
                        <div
                          className="border rounded p-3 bg-light"
                          style={{ maxHeight: '200px', overflowY: 'auto' }}
                          dangerouslySetInnerHTML={{ __html: previewContent.body || '<em>No preview available</em>' }}
                        />
                      </>
                    ) : (
                      <div className="text-muted fst-italic">
                        Select a template to see preview
                      </div>
                    )}
                  </div>
                </Col>

                <Col md={6}>
                  <div className="border-start ps-3">
                    <h6>Selected Records</h6>
                    <div style={{ maxHeight: '300px', overflowY: 'auto' }} >
                      {filteredRecords && filteredRecords.length > 0 ? (
                        <ul className="list-unstyled mt-1">
                          {(showAllRecords ? filteredRecords : filteredRecords.slice(0, 10)).map((record, index) => (
                            <li key={index} className="mb-2">
                              <div className="d-flex align-items-center">
                                <button
                                  className={`btn ${previewRecordIndex === index ? 'btn-primary' : 'btn-secondary'} btn-sm mx-1`}
                                  onClick={() => {
                                    setPreviewRecordIndex(index);
                                    if (selectedTemplate) {
                                      updatePreviewForRecord(filteredRecords[index]);
                                    }
                                  }}
                                  style={{
                                    minWidth: '30px',
                                    padding: '0',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}
                                >
                                  {index + 1}
                                </button> {(record.name && typeof record.name === 'object' ? record.name.value || record.name.label : record.name) ||
                                  (record.email && typeof record.email === 'object' ? record.email.value : record.email) ||
                                  (record.id && typeof record.id === 'object' ? record.id.value || record.id.label : record.id) || 'N/A'}
                                <span>
                                </span>
                              </div>
                            </li>
                          ))}
                          {filteredRecords.length > 10 && (
                            <>
                              {!showAllRecords && (
                                <li className="text-muted">
                                  ... and {filteredRecords.length - 10} more
                                </li>
                              )}
                              <li className="mt-2">
                                <button
                                  className="btn btn-link btn-sm p-0 text-decoration-none"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    setShowAllRecords(!showAllRecords);
                                  }}
                                  style={{ color: '#007bff' }}
                                >
                                  {showAllRecords ? 'Show Less' : `Show All ${filteredRecords.length} Records`}
                                </button>
                              </li>
                            </>
                          )}
                        </ul>
                      ) : (
                        <div className="text-muted fst-italic">
                          No records with valid email selected
                        </div>
                      )}
                    </div>

                    <div className="mt-3">
                      <h6>Summary</h6>
                      <div className="alert alert-info">
                        <p className="mb-1">
                          <strong>Records to email:</strong> {recordCount}
                        </p>
                        <p className="mb-1">
                          <strong>Template:</strong> {selectedTemplate
                            ? templates.find(t => t.id === selectedTemplate)?.name || 'Selected'
                            : 'None selected'}
                        </p>
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>
            </Card.Body>
            <Card.Footer>
              {/* <Button variant="secondary" disabled={loading}>
                Cancel
              </Button> */}
              <Button
                variant="primary"
                onClick={handleSendBulkEmail}
                disabled={loading || !selectedTemplate || recordCount === 0}
              >
                {loading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Sending...
                  </>
                ) : (
                  `Send to ${recordCount} Records`
                )}
              </Button>
            </Card.Footer>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default BulkEmailForm;
